import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowLeft, Save, Search, DollarSign, FileText, Tag, X, Upload, File, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { useGasto, useCreateGasto, useUpdateGasto } from '../../hooks/useGastos';
import { CATEGORIAS_GASTO, METODOS_PAGO } from '../../types/gasto';
import { z } from 'zod';
import { formatCurrency } from '../../lib/utils';
import api from '../../api/clients';

const gastoFormSchema = z.object({
  categoria: z.string().min(1, 'La categoría es requerida'),
  concepto: z.string().min(1, 'El concepto es requerido'),
  descripcion: z.string().optional().nullable(),
  monto: z.number().min(0.01, 'El monto debe ser mayor a 0'),
  fecha: z.string().optional(),
  metodoPago: z.string().default('efectivo'),
  comprobante: z.string().optional().nullable(),
  pdfUrl: z.string().optional().nullable(),
  observaciones: z.string().optional().nullable(),
});

type GastoFormData = z.infer<typeof gastoFormSchema>;

export default function GastoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { data: gasto, isLoading: gastoLoading } = useGasto(id ? parseInt(id) : 0);
  const createGasto = useCreateGasto();
  const updateGasto = useUpdateGasto();

  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCategoria, setSelectedCategoria] = useState<any>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfPreview, setPdfPreview] = useState<string | null>(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<GastoFormData>({
    resolver: zodResolver(gastoFormSchema),
    defaultValues: {
      categoria: '',
      concepto: '',
      descripcion: '',
      monto: 0,
      fecha: new Date().toISOString().split('T')[0],
      metodoPago: 'efectivo',
      comprobante: '',
      pdfUrl: '',
      observaciones: '',
    },
  });

  const monto = watch('monto') || 0;

  const filteredCategorias = CATEGORIAS_GASTO.filter((c) =>
    c.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectCategoria = (categoria: any) => {
    setSelectedCategoria(categoria);
    setSearchTerm(categoria.label);
    setValue('categoria', categoria.value);
    setShowDropdown(false);
  };

  const handleClearCategoria = () => {
    setSelectedCategoria(null);
    setSearchTerm('');
    setValue('categoria', '');
    setShowDropdown(false);
  };

  const handlePdfUpload = async (file: File) => {
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Solo se permiten archivos PDF');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo no debe superar los 5MB');
      return;
    }

    setUploadingPdf(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'gastos');

    try {
      const response = await api.post('/upload/pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      if (response.data?.url) {
        setPdfPreview(response.data.url);
        setValue('pdfUrl', response.data.url);
        toast.success('PDF subido exitosamente');
      }
    } catch (error) {
      toast.error('Error al subir el PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handlePdfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPdfFile(file);
      handlePdfUpload(file);
    }
    e.target.value = '';
  };

  const handleRemovePdf = () => {
    setPdfFile(null);
    setPdfPreview(null);
    setValue('pdfUrl', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (gasto && isEditing) {
      const catInfo = CATEGORIAS_GASTO.find(c => c.value === gasto.categoria);
      setValue('categoria', gasto.categoria);
      setValue('concepto', gasto.concepto);
      setValue('descripcion', gasto.descripcion || '');
      setValue('monto', Number(gasto.monto));
      setValue('fecha', gasto.fecha.split('T')[0]);
      setValue('metodoPago', gasto.metodoPago || 'efectivo');
      setValue('comprobante', gasto.comprobante || '');
      setValue('pdfUrl', gasto.pdfUrl || '');
      setValue('observaciones', gasto.observaciones || '');

      if (catInfo) {
        setSelectedCategoria(catInfo);
        setSearchTerm(catInfo.label);
      }
      if (gasto.pdfUrl) {
        setPdfPreview(gasto.pdfUrl);
      }
    }
  }, [gasto, isEditing, setValue]);

  const onSubmit = async (data: GastoFormData) => {
    try {
      const payload = {
        ...data,
        monto: Number(data.monto),
        fecha: data.fecha ? new Date(data.fecha).toISOString() : new Date().toISOString(),
        pdfUrl: data.pdfUrl || null,
      };

      if (isEditing && id) {
        await updateGasto.mutateAsync({ id: parseInt(id), data: payload });
      } else {
        await createGasto.mutateAsync(payload);
      }
      navigate('/gastos');
    } catch (error) {
      toast.error('Error al guardar el gasto');
    }
  };

  if (gastoLoading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando gasto...</div>
      </div>
    );
  }

  const fechaValue = watch('fecha');
  const fechaDisplay = fechaValue ? new Date(fechaValue).toLocaleDateString('es-AR') : '-';

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/gastos')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditing ? 'Editar Gasto' : 'Nuevo Gasto'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna principal */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FileText size={20} className="text-blue-500" />
                Datos del Gasto
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Categoría */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoría *
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      placeholder="Buscar categoría..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowDropdown(true);
                        if (!e.target.value) {
                          setSelectedCategoria(null);
                          setValue('categoria', '');
                        }
                      }}
                      onFocus={() => setShowDropdown(true)}
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={handleClearCategoria}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X size={18} />
                      </button>
                    )}

                    {/* Dropdown */}
                    {showDropdown && searchTerm && filteredCategorias.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredCategorias.map((categoria) => (
                          <button
                            key={categoria.value}
                            type="button"
                            className="w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors flex items-center gap-3 border-b border-gray-100 last:border-0"
                            onClick={() => handleSelectCategoria(categoria)}
                          >
                            <span
                              className="w-3 h-3 rounded-full shrink-0"
                              style={{ backgroundColor: categoria.color }}
                            />
                            <span className="font-medium text-gray-800">{categoria.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Mostrar categoría seleccionada */}
                  {selectedCategoria && !showDropdown && (
                    <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
                      <span
                        className="w-3 h-3 rounded-full shrink-0"
                        style={{ backgroundColor: selectedCategoria.color }}
                      />
                      <span className="text-sm text-gray-700">{selectedCategoria.label}</span>
                      <button
                        type="button"
                        onClick={handleClearCategoria}
                        className="ml-auto text-gray-400 hover:text-gray-600"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}

                  {errors.categoria && (
                    <p className="text-sm text-red-500 mt-1">{errors.categoria.message}</p>
                  )}
                </div>

                {/* Concepto */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Concepto *
                  </label>
                  <Input
                    {...register('concepto')}
                    placeholder="Ej: Carga de combustible"
                    error={errors.concepto?.message}
                  />
                </div>

                {/* Monto */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monto *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      type="number"
                      step="0.01"
                      {...register('monto', { valueAsNumber: true })}
                      placeholder="0.00"
                      className="pl-10"
                      error={errors.monto?.message}
                    />
                  </div>
                </div>

                {/* Fecha */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha
                  </label>
                  <Input
                    type="date"
                    {...register('fecha')}
                  />
                </div>

                {/* Método de Pago */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Método de Pago
                  </label>
                  <select
                    {...register('metodoPago')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {METODOS_PAGO.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Comprobante */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    N° Comprobante
                  </label>
                  <Input
                    {...register('comprobante')}
                    placeholder="Ej: FAC-001"
                  />
                </div>

                {/* PDF */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Comprobante PDF
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".pdf"
                      onChange={handlePdfChange}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPdf}
                      className="flex items-center gap-2"
                    >
                      <Upload size={16} />
                      {uploadingPdf ? 'Subiendo...' : 'Seleccionar PDF'}
                    </Button>
                    {pdfPreview && (
                      <div className="flex items-center gap-2 px-3 py-1 bg-green-50 rounded-lg border border-green-200">
                        <File size={16} className="text-green-500" />
                        <span className="text-sm text-gray-600 truncate max-w-50">
                          {pdfFile?.name || 'PDF cargado'}
                        </span>
                        <button
                          type="button"
                          onClick={handleRemovePdf}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Formatos permitidos: PDF (máx. 5MB)
                  </p>
                  {pdfPreview && (
                    <a
                      href={pdfPreview}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline mt-1 inline-block"
                    >
                      Ver PDF
                    </a>
                  )}
                </div>

                {/* Descripción */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    {...register('descripcion')}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Descripción detallada del gasto..."
                  />
                </div>

                {/* Observaciones */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observaciones
                  </label>
                  <textarea
                    {...register('observaciones')}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Observaciones adicionales..."
                  />
                </div>
              </div>
            </Card>
          </div>

          {/* Columna derecha */}
          <div>
            <Card className="p-6 sticky top-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Tag size={20} className="text-green-500" />
                Resumen
              </h2>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Categoría</span>
                  {selectedCategoria ? (
                    <span
                      className="text-sm font-medium px-2 py-0.5 rounded-full text-white"
                      style={{ backgroundColor: selectedCategoria.color }}
                    >
                      {selectedCategoria.label}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">Sin categoría</span>
                  )}
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Concepto</span>
                  <span className="font-medium text-gray-800 truncate max-w-30">
                    {watch('concepto') || '-'}
                  </span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Método de Pago</span>
                  <span className="text-sm text-gray-500 capitalize">
                    {watch('metodoPago') || 'efectivo'}
                  </span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Fecha</span>
                  <span className="text-sm text-gray-500">
                    {fechaDisplay}
                  </span>
                </div>

                {pdfPreview && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">PDF</span>
                    <a
                      href={pdfPreview}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm"
                    >
                      Ver PDF
                    </a>
                  </div>
                )}

                <div className="border-t pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-700">Total</span>
                    <span className="font-bold text-red-600 text-xl">
                      {formatCurrency(monto)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Botones */}
              <div className="flex flex-col gap-2 mt-6 pt-4 border-t">
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  <Save size={18} />
                  {isEditing ? 'Actualizar Gasto' : 'Guardar Gasto'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/gastos')}
                  className="w-full"
                >
                  Cancelar
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}